import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Image, Upload, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminResimler = () => {
  const [resimler, setResimler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    yol: "",
    aktif: true,
    resim_data: "",
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchResimler = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/resimler`, {
        headers: getAuthHeader(),
      });
      setResimler(response.data);
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Resimler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResimler();
  }, [fetchResimler]);

  const openAddDialog = () => {
    setFormData({
      yol: "",
      aktif: true,
      resim_data: "",
    });
    setPreviewUrl("");
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Lütfen bir resim dosyası seçin");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dosya boyutu 5MB'dan küçük olmalıdır");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(",")[1];
      setFormData({
        ...formData,
        yol: file.name,
        resim_data: base64,
      });
      setPreviewUrl(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.resim_data) {
      toast.error("Lütfen bir resim seçin");
      return;
    }

    setUploading(true);

    try {
      await axios.post(`${API}/admin/resimler`, formData, {
        headers: getAuthHeader(),
      });
      toast.success("Resim eklendi");
      setDialogOpen(false);
      fetchResimler();
    } catch (error) {
      console.error("Error adding image:", error);
      toast.error("Resim eklenirken hata oluştu");
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = async (resim) => {
    try {
      await axios.put(
        `${API}/admin/resimler/toggle`,
        { id: resim.id, aktif: !resim.aktif },
        { headers: getAuthHeader() }
      );
      toast.success(resim.aktif ? "Resim pasif yapıldı" : "Resim aktif yapıldı");
      fetchResimler();
    } catch (error) {
      console.error("Error toggling image:", error);
      toast.error("Resim durumu değiştirilirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu resmi silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/admin/resimler/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Resim silindi");
      fetchResimler();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Resim silinirken hata oluştu");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-resimler-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-pink-100 rounded-xl flex items-center justify-center">
            <Image className="w-5 h-5 lg:w-6 lg:h-6 text-pink-600" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Resim Yönetimi
            </h1>
            <p className="text-slate-500 text-xs lg:text-sm">{resimler.length} resim listeleniyor</p>
          </div>
        </div>
        <Button onClick={openAddDialog} className="btn-primary text-sm lg:text-base" data-testid="add-resim-button">
          <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
          <span className="ml-1">Yeni Resim</span>
        </Button>
      </div>

      {/* Images Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-6">
        {resimler.map((resim) => (
          <div 
            key={resim.id}
            className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all ${
              resim.aktif ? 'border-slate-200' : 'border-slate-300 opacity-60'
            }`}
            data-testid={`resim-card-${resim.id}`}
          >
            <div className="aspect-video relative bg-slate-100">
              {resim.resim_data ? (
                <img
                  src={`data:image/jpeg;base64,${resim.resim_data}`}
                  alt={resim.yol}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-8 h-8 lg:w-12 lg:h-12 text-slate-300" />
                </div>
              )}
              {!resim.aktif && (
                <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
                  <EyeOff className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                </div>
              )}
            </div>
            <div className="p-2 lg:p-4">
              <p className="text-[10px] lg:text-sm text-slate-600 truncate mb-2 lg:mb-3">{resim.yol}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 lg:gap-2">
                  <Switch
                    checked={resim.aktif}
                    onCheckedChange={() => handleToggle(resim)}
                    data-testid={`toggle-resim-${resim.id}`}
                  />
                  <span className="text-[10px] lg:text-xs text-slate-500">
                    {resim.aktif ? "Aktif" : "Pasif"}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(resim.id)}
                  className="p-1 lg:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-resim-${resim.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {resimler.length === 0 && (
        <div className="text-center py-8 lg:py-12 bg-white rounded-xl border border-slate-200">
          <Image className="w-10 h-10 lg:w-12 lg:h-12 text-slate-300 mx-auto mb-3 lg:mb-4" />
          <p className="text-slate-500 text-sm lg:text-base">Henüz resim bulunmuyor</p>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Yeni Resim Ekle
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div
              className={`border-2 border-dashed rounded-xl p-6 lg:p-8 text-center cursor-pointer transition-colors ${
                previewUrl ? 'border-amber-300 bg-amber-50' : 'border-slate-300 hover:border-amber-400'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
              
              {previewUrl ? (
                <div className="space-y-2 lg:space-y-3">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-36 lg:max-h-48 mx-auto rounded-lg shadow-sm"
                  />
                  <p className="text-xs lg:text-sm text-slate-600">{formData.yol}</p>
                  <p className="text-[10px] lg:text-xs text-amber-600">Değiştirmek için tıklayın</p>
                </div>
              ) : (
                <div className="space-y-2 lg:space-y-3">
                  <Upload className="w-10 h-10 lg:w-12 lg:h-12 text-slate-400 mx-auto" />
                  <div>
                    <p className="text-slate-600 font-medium text-sm lg:text-base">Resim yüklemek için tıklayın</p>
                    <p className="text-xs lg:text-sm text-slate-400">PNG, JPG, JPEG (max 5MB)</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="aktif"
                checked={formData.aktif}
                onCheckedChange={(checked) => setFormData({ ...formData, aktif: checked })}
                data-testid="switch-aktif"
              />
              <Label htmlFor="aktif" className="text-sm">Aktif</Label>
            </div>

            <div className="flex justify-end gap-2 lg:gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="text-sm"
                data-testid="cancel-button"
              >
                İptal
              </Button>
              <Button 
                type="submit" 
                disabled={!formData.resim_data || uploading}
                className="btn-primary text-sm"
                data-testid="upload-button"
              >
                {uploading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Yükleniyor...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-1" />
                    Yükle
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResimler;
